import {
  enqueueNativeTelemetry,
  markNativeTelemetryFlushed,
  resolveNativeLoopFingerprint,
} from './NativeLoopOfflineBridge';

export const OFFLINE_TELEMETRY_QUEUE_PREFIX = 'trillboards:offline-telemetry-queue:v1';
export const OFFLINE_TELEMETRY_QUEUE_LIMIT = 2160;
export const OFFLINE_TELEMETRY_QUEUE_TTL_MS = 72 * 60 * 60 * 1000;
const OFFLINE_TELEMETRY_REPLAY_BATCH_SIZE = 4;

export const buildOfflineTelemetryQueueKey = (screenId) =>
  `${OFFLINE_TELEMETRY_QUEUE_PREFIX}:${String(screenId || 'unknown')}`;

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (_) {}
  return null;
};

const now = () => Date.now();

export const isBrowserOnline = () => {
  try {
    if (typeof window !== 'undefined' && window.__TRILLBOARDS_FORCE_OFFLINE_TELEMETRY_QUEUE === true) {
      return false;
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return false;
    }
  } catch (_) {}
  return true;
};

const parseQueue = (raw) => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.items) ? parsed.items : [];
  } catch (_) {
    return [];
  }
};

const pruneQueue = (items, nowMs = now()) => items
  .filter((item) => item && typeof item === 'object' && item.payload && item.url)
  .filter((item) => !item.expires_at_ms || Number(item.expires_at_ms) > nowMs)
  .slice(-OFFLINE_TELEMETRY_QUEUE_LIMIT);

const writeQueue = (screenId, items, storage = getStorage(), nowMs = now()) => {
  if (!storage) return pruneQueue(items, nowMs);
  const pruned = pruneQueue(items, nowMs);
  try {
    storage.setItem(buildOfflineTelemetryQueueKey(screenId), JSON.stringify({
      version: 1,
      updated_at_ms: nowMs,
      items: pruned,
    }));
  } catch (_) {}
  return pruned;
};

export const readOfflineTelemetryQueue = (screenId, storage = getStorage(), nowMs = now()) => {
  if (!storage) return [];
  const key = buildOfflineTelemetryQueueKey(screenId);
  const queue = pruneQueue(parseQueue(storage.getItem(key)), nowMs);
  if (queue.length !== parseQueue(storage.getItem(key)).length) {
    writeQueue(screenId, queue, storage, nowMs);
  }
  return queue;
};

export const getQueuedTelemetryCount = (screenId, storage = getStorage()) =>
  readOfflineTelemetryQueue(screenId, storage).length;

export const buildOfflineTelemetryEventKey = (payload = {}) => {
  const loop = payload.loop_playback || null;
  if (loop) {
    return [
      'loop_playback',
      loop.session_id || '',
      loop.transition_timestamp_ms || '',
      loop.plan_id ?? '',
      loop.cycle_number ?? '',
      loop.slot_index ?? '',
      loop.tier || '',
      loop.reservation_id || '',
      loop.selected_creative_id || '',
    ].join('|');
  }

  const bootstrap = payload.bootstrap || null;
  if (bootstrap) {
    return [
      'bootstrap',
      bootstrap.state || '',
      payload.collected_at || '',
      bootstrap.details?.reason || '',
    ].join('|');
  }

  return [
    'browser_signal',
    payload.collected_at || '',
    payload.user_agent || '',
  ].join('|');
};

export const withQueuedEventCount = (payload = {}, queuedEventCount = 0) => {
  const count = Math.max(0, Number(queuedEventCount) || 0);
  const cacheStatus = {
    ...(payload.cache_status || {}),
    queued_event_count: count,
  };
  const nextPayload = {
    ...payload,
    cache_status: cacheStatus,
  };

  if (payload.bootstrap) {
    nextPayload.bootstrap = {
      ...payload.bootstrap,
      details: {
        ...(payload.bootstrap.details || {}),
        queued_event_count: count,
      },
      cache_status: {
        ...(payload.bootstrap.cache_status || {}),
        queued_event_count: count,
      },
    };
  }

  return nextPayload;
};

const withOfflineState = (payload = {}) => {
  const nextPayload = {
    ...payload,
    cache_status: {
      ...(payload.cache_status || {}),
      online_state: 'offline',
    },
  };

  if (payload.bootstrap) {
    nextPayload.bootstrap = {
      ...payload.bootstrap,
      details: {
        ...(payload.bootstrap.details || {}),
        online_state: 'offline',
      },
      cache_status: {
        ...(payload.bootstrap.cache_status || {}),
        online_state: 'offline',
      },
    };
  }

  return nextPayload;
};

export const enqueueOfflineTelemetry = (screenId, url, payload, options = {}) => {
  const storage = options.storage || getStorage();
  const nowMs = Number(options.nowMs) || now();
  const eventKey = options.eventKey || buildOfflineTelemetryEventKey(payload);
  const existing = readOfflineTelemetryQueue(screenId, storage, nowMs);
  const withoutDuplicate = existing.filter((item) => item.event_key !== eventKey);
  const queuedPayload = withQueuedEventCount(payload, withoutDuplicate.length + 1);
  const item = {
    event_key: eventKey,
    url,
    payload: queuedPayload,
    queued_at_ms: nowMs,
    expires_at_ms: nowMs + OFFLINE_TELEMETRY_QUEUE_TTL_MS,
    attempt_count: Number(options.attemptCount) || 0,
  };
  const items = writeQueue(screenId, [...withoutDuplicate, item], storage, nowMs);
  enqueueNativeTelemetry(screenId, options.fingerprint, url, queuedPayload, eventKey, {
    queuedAtMs: nowMs,
    attemptCount: item.attempt_count,
  });

  return {
    queued: true,
    eventKey,
    queuedEventCount: items.length,
  };
};

const getTelemetryFlushBeacon = (options = {}) => {
  if (options.preferBeacon === false) return null;
  if (typeof options.beaconImpl === 'function') return options.beaconImpl;

  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    if (nav && typeof nav.sendBeacon === 'function') {
      return nav.sendBeacon.bind(nav);
    }
  } catch (_) {}

  return null;
};

const hasTelemetryFlushTransport = (options = {}) => {
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const beaconImpl = getTelemetryFlushBeacon(options);
  return Boolean(fetchImpl || (options.preferBeacon === true && beaconImpl && typeof Blob === 'function'));
};

const postTelemetryPayload = async (item, options = {}) => {
  const body = JSON.stringify(item.payload);
  const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const postWithFetch = async () => {
    if (!fetchImpl) return false;
    try {
      const response = await fetchImpl(item.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
      return response?.ok === true || response?.type === 'opaque';
    } catch (_) {}
    return false;
  };

  if (options.preferBeacon !== true) {
    return postWithFetch();
  }

  const beaconImpl = getTelemetryFlushBeacon(options);
  if (beaconImpl && typeof Blob === 'function') {
    try {
      const blob = new Blob([body], { type: 'application/json' });
      if (beaconImpl(item.url, blob)) return true;
    } catch (_) {}
  }

  return postWithFetch();
};

export const flushOfflineTelemetryQueue = async (screenId, options = {}) => {
  const storage = options.storage || getStorage();
  const nowMs = Number(options.nowMs) || now();
  const maxBatch = Math.max(1, Number(options.maxBatch) || OFFLINE_TELEMETRY_REPLAY_BATCH_SIZE);
  const maxBatches = Math.max(1, Number(options.maxBatches) || 24);

  if (!storage || !hasTelemetryFlushTransport(options) || !isBrowserOnline()) {
    return {
      flushed: 0,
      remaining: getQueuedTelemetryCount(screenId, storage),
      skipped: true,
    };
  }

  let flushed = 0;
  let remaining = 0;

  for (let batchIndex = 0; batchIndex < maxBatches; batchIndex += 1) {
    const queue = readOfflineTelemetryQueue(screenId, storage, nowMs);
    if (!queue.length) {
      return { flushed, remaining: 0, skipped: false };
    }

    const batch = queue.slice(0, maxBatch);
    const rest = queue.slice(maxBatch);
    const failed = [];
    const flushedEventKeys = [];

    for (const item of batch) {
      try {
        const ok = await postTelemetryPayload(item, options);
        if (ok) {
          flushed += 1;
          flushedEventKeys.push(item.event_key);
        } else {
          failed.push({
            ...item,
            attempt_count: Number(item.attempt_count || 0) + 1,
          });
        }
      } catch (_) {
        failed.push({
          ...item,
          attempt_count: Number(item.attempt_count || 0) + 1,
        });
      }
    }

    const remainingItems = writeQueue(screenId, [...failed, ...rest], storage, nowMs);
    remaining = remainingItems.length;
    if (flushedEventKeys.length > 0) {
      markNativeTelemetryFlushed(screenId, flushedEventKeys);
    }

    if (failed.length > 0 || remaining <= 0 || batch.length < maxBatch) {
      return {
        flushed,
        remaining,
        skipped: false,
      };
    }
  }

  return {
    flushed,
    remaining,
    skipped: false,
  };
};

export const removeQueuedTelemetryEvents = (screenId, eventKeys, options = {}) => {
  const storage = options.storage || getStorage();
  if (!storage || !Array.isArray(eventKeys) || eventKeys.length === 0) {
    return {
      removed: 0,
      remaining: getQueuedTelemetryCount(screenId, storage),
    };
  }

  const keySet = new Set(eventKeys.map((key) => String(key || '').trim()).filter(Boolean));
  if (keySet.size === 0) {
    return {
      removed: 0,
      remaining: getQueuedTelemetryCount(screenId, storage),
    };
  }

  const queue = readOfflineTelemetryQueue(screenId, storage, options.nowMs);
  const remainingItems = queue.filter((item) => !keySet.has(item.event_key));
  if (remainingItems.length === queue.length) {
    return { removed: 0, remaining: queue.length };
  }

  const written = writeQueue(screenId, remainingItems, storage, Number(options.nowMs) || now());
  return {
    removed: queue.length - written.length,
    remaining: written.length,
  };
};

export const sendBrowserSignalWithOfflineQueue = (screenId, url, payload, options = {}) => {
  const storage = options.storage || getStorage();
  const online = isBrowserOnline();
  const queuedCount = getQueuedTelemetryCount(screenId, storage);
  const fingerprint = resolveNativeLoopFingerprint(options.fingerprint);

  if (!online) {
    return enqueueOfflineTelemetry(screenId, url, withQueuedEventCount(withOfflineState(payload), queuedCount + 1), {
      storage,
      nowMs: options.nowMs,
      fingerprint,
    });
  }

  const dispatch = (queuedEventCount = getQueuedTelemetryCount(screenId, storage)) => {
    const payloadWithQueueCount = withQueuedEventCount(payload, queuedEventCount);
    const body = JSON.stringify(payloadWithQueueCount);
    const nav = typeof navigator !== 'undefined' ? navigator : null;

    if (options.preferBeacon !== false && nav && typeof nav.sendBeacon === 'function' && typeof Blob === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (nav.sendBeacon(url, blob)) {
        return { sent: true, queued: false, via: 'beacon', queuedEventCount };
      }
    }

    const fetchImpl = options.fetchImpl || (typeof fetch === 'function' ? fetch : null);
    if (!fetchImpl) {
      return enqueueOfflineTelemetry(screenId, url, payloadWithQueueCount, { storage, nowMs: options.nowMs, fingerprint });
    }

    fetchImpl(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).then((response) => {
      if (!response?.ok) {
        enqueueOfflineTelemetry(screenId, url, payloadWithQueueCount, { storage, fingerprint });
      }
    }).catch(() => {
      enqueueOfflineTelemetry(screenId, url, payloadWithQueueCount, { storage, fingerprint });
    });

    return { sent: true, queued: false, via: 'fetch', queuedEventCount };
  };

  if (!options.skipFlush && queuedCount > 0) {
    flushOfflineTelemetryQueue(screenId, {
      storage,
      fetchImpl: options.fetchImpl,
      maxBatch: options.maxBatch || OFFLINE_TELEMETRY_REPLAY_BATCH_SIZE,
      maxBatches: options.maxBatches || 1,
      preferBeacon: false,
      fingerprint,
    }).finally(() => {
      dispatch();
      if (typeof options.onFlush === 'function') {
        options.onFlush(getQueuedTelemetryCount(screenId, storage));
      }
    });
    return { sent: false, queued: false, flushing: true, queuedEventCount: queuedCount };
  }

  return dispatch(queuedCount);
};
